import { Section } from '@/javascript/music-objects';
import { ProjectTrack } from '@/javascript/project-objects';
import { Text } from '@mantine/core';
import React from 'react';
import TimelineItem from './TimelineItem';
import { TrackClef } from './TrackClef';

export interface TrackRenderProps {
	trackIndex: number
	track: ProjectTrack;
	tighten: boolean;
	trackHeight: number;
	measureWidth: number;
	trackLabelWidth: number;
	selectedItems: string[];
	addItemToSelectedItems: (id: string) => void;
	removeItemFromSelectedItems: (id: string) => void;
	clearSelectedItems: () => void;
	startDraggingItems: (itemActionLeaderId: string, distance: number) => void;
	startTrimmingItemsLeft: (itemActionLeaderId: string) => void;
	startTrimmingItemsRight: (itemActionLeaderId: string) => void;
	chopMode: boolean;
	chopItem: (event: { pageX: number; }, id: string) => void;
	notationView: boolean;
	draggingSections: Section[] | null;
	isTutorialTrack: boolean;
	isPhoneOrSmaller: boolean;
	isTabletOrSmaller: boolean;
}

export const Track: React.FC<TrackRenderProps> = React.memo((props: TrackRenderProps) => {
	const {
		trackIndex,
		track,
		tighten,
		measureWidth,
		trackLabelWidth,
		trackHeight,
		selectedItems,
		addItemToSelectedItems,
		removeItemFromSelectedItems,
		clearSelectedItems,
		startDraggingItems,
		startTrimmingItemsLeft,
		startTrimmingItemsRight,
		chopMode,
		chopItem,
		notationView,
		draggingSections,
		isPhoneOrSmaller,
		isTabletOrSmaller,
	} = props;

	return (
		<div
			className={`timeline-track ${track.ghosted ? 'timeline-track-ghost' : ''}`}
			style={{
				height: trackHeight,
				pointerEvents: draggingSections?.length ? 'none' : 'auto'
			}}
		>
			<div
				style={{
					width: trackLabelWidth,
					height: trackHeight,
					justifyContent: `${notationView ? 'right' : 'center'}`
				}}
				className={'track-label'}
			>
				{notationView && !isPhoneOrSmaller ?
					<>
						<Text
							size="sm"
						>
							{track.getInstrument().abbreviation.substring(0, isTabletOrSmaller ? 3 : 5)}
						</Text>
						<TrackClef
							clef={track.getInstrument().clef}
						/>
					</>
					:
					<Text
						fz={isTabletOrSmaller ? 10 : "xs"}
					>
						{track.getInstrument().name}
					</Text>
				}
			</div>
			<div
				className="timeline-track-items-container"
				style={{
					height: trackHeight,
					left: trackLabelWidth
				}}
			>
				{track.items.map((item) =>
					<TimelineItem
						trackIndex={trackIndex}
						key={item.id}
						item={item}
						tighten={tighten}
						measureWidth={measureWidth}
						addItemToSelectedItems={addItemToSelectedItems}
						removeItemFromSelectedItems={removeItemFromSelectedItems}
						clearSelectedItems={clearSelectedItems}
						startDraggingItems={startDraggingItems}
						startTrimmingItemsLeft={startTrimmingItemsLeft}
						startTrimmingItemsRight={startTrimmingItemsRight}
						selected={selectedItems.includes(item.id)}
						chopMode={chopMode}
						chopItem={chopItem}
						notationView={notationView}
						isTutorialItem={item.mostRecentInProject}
						height={trackHeight} // Subtract 2 to account for the border
						isPhoneOrSmaller={isPhoneOrSmaller}
						isTabletOrSmaller={isTabletOrSmaller}
					/>
				)}
			</div>
		</div>
	);
}, (prevProps, nextProps) => {
	const areEqual = prevProps.track.items.length === nextProps.track.items.length &&
		prevProps.tighten === nextProps.tighten &&
		prevProps.measureWidth === nextProps.measureWidth &&
		prevProps.trackHeight === nextProps.trackHeight &&
		prevProps.selectedItems.length === nextProps.selectedItems.length &&
		prevProps.selectedItems.every((item, index) => item === nextProps.selectedItems[index]) &&
		prevProps.draggingSections?.length === nextProps.draggingSections?.length &&
		prevProps.isTutorialTrack === nextProps.isTutorialTrack &&
		prevProps.trackLabelWidth === nextProps.trackLabelWidth &&
		prevProps.chopMode === nextProps.chopMode &&
		prevProps.track.getHash() === nextProps.track.getHash() &&
		prevProps.isPhoneOrSmaller === nextProps.isPhoneOrSmaller &&
		prevProps.isTabletOrSmaller === nextProps.isTabletOrSmaller;
	return areEqual;
}
);
